import {QueryKey, QueryOptions, useQuery} from '@tanstack/react-query'
import {useFormik} from 'formik'
import queryString from 'query-string'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'
import FilterRedux, {IFilterState} from '../store/Filter/FilterRedux'
import {RootState} from '../store/RootReducer'
import {hasArray} from '../utils/format.utils'
import {useDebounceEffect} from './debounce.hook'
import {useErrorQuery} from './error-query.hook'
import {useFilterCount} from './filter-count.hook'

type UsePaginatorOptions<T, Q> = {
  queryKey: string[]
  queryFn: (params: Q) => any
  enabled?: boolean
  queryOptions?: QueryOptions<T> & QueryKey
  filterForm?: any
  screen?: string
}

type DefaultValue<Q> = {
  page: number
  size: number
} & Q

function createDefaultValue<Q>(queryDefaults: Q): DefaultValue<Q> {
  return {
    page: 0,
    size: 10,
    ...queryDefaults,
  }
}

export function usePaginationQuery<T, Q>(
  p: UsePaginatorOptions<T, Q>,
  defaultValue: DefaultValue<Q> = createDefaultValue<Q>({} as Q)
) {
  const dispatch = useDispatch()

  const {errorTemp} = useErrorQuery()
  const location = useLocation()
  const history = useHistory()

  const [isShow, setIsShow] = useState(false)
  const [filterCount, setFilterCount] = useState<number>(0)
  const [isEmptyTable, setIsEmptyTable] = useState<boolean>(false)

  const handleEmptyTable = useCallback((total?: number) => {
    setIsEmptyTable(total === 0)
  }, [])

  const parseUrlParams = () => {
    const parsed = queryString.parse(location.search)

    return {
      ...defaultValue,
      ...parsed,
      page: parsed.page ? Number(parsed.page) : defaultValue.page,
      size: parsed.size ? Number(parsed.size) : defaultValue.size,
    }
  }

  const syncUrlWithState = (newState: typeof state) => {
    const queryParams = {
      ...newState,
      page: newState.page,
      size: newState.size,
    }

    const newSearch = queryString.stringify(queryParams, {encode: false})

    if (location.search !== `?${newSearch}`) {
      history.replace({search: newSearch})
    }
  }

  const [total, setTotal] = useState(0)
  const [state, setState] = useState(() => parseUrlParams())

  useEffect(() => {
    syncUrlWithState(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const renderParams = useMemo(() => {
    const {page, size, ...rest} = state
    return {page, size, ...rest}
  }, [state])

  const query = useQuery<T>({
    queryKey: [...p.queryKey, renderParams],
    queryFn: async () => {
      const payload = await p.queryFn(renderParams as Q)
      const data = payload.data.response_output?.list?.content

      const pagination = payload.data.response_output?.list?.pagination

      setTotal(pagination.total)

      const {size, page, ...restQuery} = state
      if (Object.keys(restQuery).length === 0) handleEmptyTable(pagination?.total)

      return data
    },
    onError: (e) => {
      errorTemp(e)
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: p.enabled,
    ...p.queryOptions,
  })

  const setPage = useCallback(
    (page: number) => {
      setState((prev) => {
        if (total > 0 && !Math.ceil(total / prev.size)) return prev
        return {...prev, page}
      })
    },
    [total]
  )

  const setSize = useCallback((size: number) => {
    setState((prev) => ({
      ...prev,
      page: 0,
      size,
    }))
  }, [])

  const setQuery = useCallback(<K extends keyof Q>(key: K, value: Q[K]) => {
    setState((prev) => ({
      ...prev,
      page: 0,
      [key]: value,
    }))
  }, [])

  const setBulkQuery = useCallback((fields: Partial<Q>) => {
    const newFields = Object.entries(fields).reduce((acc: any, [key, fieldValue]: any) => {
      if (key in fields) {
        if (Array.isArray(fieldValue)) {
          const isValidSelectArray = fieldValue.every(
            (item) => item && typeof item === 'object' && 'value' in item
          )
          acc[key as keyof Q] = isValidSelectArray
            ? fieldValue.map((item: any) => item.value)
            : fieldValue
        } else {
          acc[key as keyof Q] = fieldValue
        }
      }
      return acc
    }, {} as Partial<Q>)

    setState((prev) => ({
      ...prev,
      ...newFields,
    }))
  }, [])

  const setLocationFilter = useCallback(
    (fields: Partial<Q>, path: string) => {
      dispatch(FilterRedux.actions.setFilter({filter: fields, location: path}))
    },
    [dispatch]
  )

  const setRemoveFilter = useCallback(() => {
    dispatch(FilterRedux.actions.setRemoveFilter())
  }, [dispatch])

  const formik = useFormik({
    initialValues: p?.filterForm ?? {},
    onSubmit: (values) => applyFilterFn(values),
  })

  const {tempFilterCount} = useFilterCount(formik)

  const applyFilterFn = useCallback(
    (params: Q) => {
      setIsShow(false)
      setFilterCount(tempFilterCount)
      setBulkQuery(params)
      setLocationFilter(params, p?.screen ?? '/')
      setPage(0)
    },
    [setBulkQuery, setLocationFilter, setIsShow, setFilterCount, tempFilterCount, p.screen, setPage]
  )

  const resetFilterFn = useCallback(() => {
    const {size, page, search, ...restQuery} = state as any

    const data = Object.entries(restQuery).map(([key]) => {
      return {
        [key]: undefined,
      }
    })

    const result = Object.assign({}, ...data)

    formik.resetForm()
    setFilterCount(0)
    setIsShow(false)
    setBulkQuery(result)
    setRemoveFilter()
  }, [setBulkQuery, setRemoveFilter, setIsShow, setFilterCount, formik, state])

  const searchFn = useDebounceEffect(
    useCallback(
      (search?: string) => {
        setBulkQuery({search} as Q)
        setPage(0)
      },
      [setBulkQuery, setPage]
    ),
    500
  )

  const {filter: filterRedux} = useSelector<RootState>(
    ({filter}) => filter,
    shallowEqual
  ) as IFilterState

  useEffect(() => {
    if (filterRedux) {
      Object.entries(filterRedux).forEach(([key, value]: any) => {
        if (hasArray(value)) formik.setFieldValue(key, value ?? [])
        else formik.setFieldValue(key, value)
      })
    }
    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!filterRedux) {
      let initialCount = 0

      const {size, page, search, total, sort, order, ...restQuery} = state as any

      Object.values(restQuery).forEach((value: any) => {
        if (hasArray(value)) {
          initialCount += value.length
        } else if (value === undefined || value === '') {
          initialCount += 0
        } else {
          initialCount += 1
        }
      })

      setFilterCount(initialCount)
    }

    if (filterRedux) {
      let initialCount = 0

      Object.values(filterRedux).forEach((value: any) => {
        if (hasArray(value)) {
          initialCount += value.length
        } else if (value === undefined || value === '') {
          initialCount += 0
        } else if (value.length === 0) {
          initialCount += 0
        } else {
          initialCount += 1
        }
      })

      setFilterCount(initialCount)
    }
  }, [filterRedux, state])

  return {
    state: {...state, total},
    setPage,
    setSize,
    setTotal,
    setQuery,
    setBulkQuery,
    setLocationFilter,
    setRemoveFilter,
    setState,
    isEmptyTable,
    formik,
    searchFn,
    applyFilterFn,
    resetFilterFn,
    isShow,
    setIsShow,
    filterCount,
    ...query,
  }
}
